import { useCallback, useEffect, useRef, useState } from 'react';

import { useAppDispatch } from '../store/hooks';
import { speechToText } from '../store/features/dashboard';
import { renderErrorToast } from '../utility/helpers';

export type SpeechToTextStatus = 'idle' | 'recording' | 'transcribing';

/** Animated label shown in the chat textarea while Whisper STT runs. */
export const TRANSCRIBING_BASE = 'Transcribing';
/** Load then unload dots:  . .. ... .. .  */
export const TRANSCRIBING_DOT_FRAMES = ['', '.', '..', '...', '..', '.'] as const;

export const isTranscribingLabel = (value: string): boolean =>
  value === TRANSCRIBING_BASE ||
  (value.startsWith(TRANSCRIBING_BASE) && value.length <= TRANSCRIBING_BASE.length + 3);


const MAX_RECORDING_MS = 60_000;
const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

const pickMimeType = (): string => {
  if (typeof MediaRecorder === 'undefined') return '';
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
    'audio/ogg;codecs=opus',
    'audio/ogg',
  ];
  return candidates.find((type) => MediaRecorder.isTypeSupported(type)) || '';
};

const extensionForMime = (mime: string): string => {
  if (mime.includes('webm')) return 'webm';
  if (mime.includes('mp4') || mime.includes('m4a')) return 'm4a';
  if (mime.includes('ogg')) return 'ogg';
  if (mime.includes('wav')) return 'wav';
  if (mime.includes('mpeg') || mime.includes('mp3')) return 'mp3';
  return 'webm';
};

/**
 * Records microphone audio and posts it to POST /ai/speech-to-text/.
 */
export const useSpeechToText = (options?: {
  language?: string;
  prompt?: string;
  onTranscript?: (text: string) => void;
}) => {
  const dispatch = useAppDispatch();
  const [status, setStatus] = useState<SpeechToTextStatus>('idle');
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const maxTimerRef = useRef<number | null>(null);
  const startingRef = useRef(false);
  const statusRef = useRef<SpeechToTextStatus>('idle');
  const optionsRef = useRef(options);
  optionsRef.current = options;
  statusRef.current = status;

  const cleanupStream = useCallback(() => {
    if (maxTimerRef.current) {
      window.clearTimeout(maxTimerRef.current);
      maxTimerRef.current = null;
    }
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    mediaRecorderRef.current = null;
    chunksRef.current = [];
  }, []);

  useEffect(() => () => cleanupStream(), [cleanupStream]);

  const transcribeBlob = useCallback(
    async (blob: Blob, mimeType: string) => {
      if (!blob.size) {
        renderErrorToast('No audio captured. Please try again.');
        setStatus('idle');
        return;
      }
      if (blob.size > MAX_UPLOAD_BYTES) {
        renderErrorToast('Recording is too large (max 25 MB).');
        setStatus('idle');
        return;
      }

      setStatus('transcribing');
      try {
        const ext = extensionForMime(mimeType || blob.type);
        const res: any = await dispatch(
          speechToText({
            audio: blob,
            filename: `recording.${ext}`,
            language: optionsRef.current?.language,
            prompt: optionsRef.current?.prompt,
          }),
        ).unwrap();

        const text = String(res?.data?.text ?? res?.text ?? '').trim();
        if (!text) {
          renderErrorToast('Could not transcribe audio. Please try again.');
          setStatus('idle');
          return;
        }
        optionsRef.current?.onTranscript?.(text);
      } catch (error: any) {
        const message =
          error?.detail ||
          error?.message ||
          (typeof error === 'string' ? error : null) ||
          'Speech-to-text failed. Please try again.';
        renderErrorToast(message);
      } finally {
        setStatus('idle');
      }
    },
    [dispatch],
  );

  const stopRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state === 'inactive') {
      cleanupStream();
      startingRef.current = false;
      setStatus('idle');
      return;
    }
    recorder.stop();
  }, [cleanupStream]);

  const startRecording = useCallback(async () => {
    if (startingRef.current || statusRef.current === 'recording' || statusRef.current === 'transcribing') {
      return;
    }
    if (typeof window === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      renderErrorToast('Microphone is not supported in this browser.');
      return;
    }
    if (typeof MediaRecorder === 'undefined') {
      renderErrorToast('Audio recording is not supported in this browser.');
      return;
    }

    startingRef.current = true;
    // Stop any leftover stream from a prior attempt before opening a new one.
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (!startingRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      streamRef.current = stream;
      chunksRef.current = [];

      const mimeType = pickMimeType();
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (event: BlobEvent) => {
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      recorder.onerror = () => {
        renderErrorToast('Recording failed. Please try again.');
        cleanupStream();
        startingRef.current = false;
        setStatus('idle');
      };

      recorder.onstop = () => {
        const type = recorder.mimeType || mimeType || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type });
        cleanupStream();
        startingRef.current = false;
        void transcribeBlob(blob, type);
      };

      recorder.start(250);
      setStatus('recording');
      startingRef.current = false;
      maxTimerRef.current = window.setTimeout(() => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
          mediaRecorderRef.current.stop();
        }
      }, MAX_RECORDING_MS);
    } catch {
      cleanupStream();
      startingRef.current = false;
      setStatus('idle');
      renderErrorToast('Microphone permission is required for voice input.');
    }
  }, [cleanupStream, transcribeBlob]);

  const toggleRecording = useCallback(() => {
    if (startingRef.current || status === 'transcribing') return;
    if (status === 'recording') {
      stopRecording();
      return;
    }
    void startRecording();
  }, [status, startRecording, stopRecording]);

  return {
    status,
    isRecording: status === 'recording',
    isTranscribing: status === 'transcribing',
    toggleRecording,
    stopRecording,
    startRecording,
  };
};
